import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { updateUserSettings } from "@app/analytics/preferences/api";
import { postRequestPasswordReset } from "@app/common/auth/api";
import useAuth from "@app/common/auth/useAuth";
import { showToast } from "@app/common/hooks/useToasts";
import type { UserSettings } from "@app/common/types";

const useAccountSettings = () => {
  const { user, fetchAndSetUser } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["accountSettings", user?.id];

  const resendEmailVerification = useMutation({
    mutationFn: async () => updateUserSettings({ email: user?.email }),
    onError: () => {
      showToast({
        severity: "error",
        message: `A verification email could not be sent. Please try again or contact support@read.ai.`,
      });
    },
    onSuccess: () => {
      showToast({
        severity: "success",
        message: `A verification email has been resent to ${user!.email}.`,
      });
    },
  });

  const useRequestPasswordReset = useMutation({
    mutationFn: async ({
      email,
      recaptchaValue,
    }: {
      email: string;
      recaptchaValue: string | null;
    }) => await postRequestPasswordReset(email, recaptchaValue, true),
  });

  const useUpdateEmail = useMutation({
    mutationFn: async (newEmail: string) =>
      await updateUserSettings({ email: newEmail }),
  });

  const useUpdateProfileInfo = useMutation({
    mutationFn: async (newInfo: { firstName: string; lastName: string }) =>
      await updateUserSettings(newInfo),
  });

  const updateAccountSettingsMutation = useMutation<
    Partial<UserSettings>,
    unknown,
    Partial<UserSettings>
  >({
    mutationFn: updateUserSettings,
    onMutate: async (newUserSettings: Partial<UserSettings>) => {
      await queryClient.cancelQueries({ queryKey });
      const prevUserSettings: Partial<UserSettings> =
        queryClient.getQueryData(queryKey) ?? {};

      const combinedUserSettings = {
        ...prevUserSettings,
        ...newUserSettings,
      };
      queryClient.setQueryData(queryKey, combinedUserSettings);
    },
  });

  const accountSettingsQuery = useQuery<UserSettings>({
    queryKey: ["accountSettings", user?.id],
    queryFn: async (): Promise<UserSettings> => {
      return await fetchAndSetUser();
    },
  });

  return {
    resendEmailVerification,
    useRequestPasswordReset,
    useUpdateEmail,
    useUpdateProfileInfo,
    updateAccountSettingsMutation,
    accountSettingsQuery,
    accountSettings: accountSettingsQuery.data,
  };
};
export default useAccountSettings;
